import React from 'react'
import {
  Box,
  Display,
  withCitrusTheme,
  Image as CitrusImage,
  Text,
  FontWeight,
  AlignItems,
  JustifyContent,
  Icon,
  Color,
  FlexDirection,
  IconVariant,
  IconNames,
  Caption2,
  Heading6,
  Body2,
} from '@gousto-internal/citrus-react'
import { Recipe, RecipeCoreId, RecipeUUID } from '@library/menu-service'
import { getDefaultImage, getMoodImage, getSrcSet } from '../../../utils/formatters'

const ClickableIcon = ({
  name,
  onClick,
  dataNoCode,
}: {
  name: IconNames
  onClick: () => void
  dataNoCode?: {
    add?: string
  }
}) => (
  <Box
    display={Display.Flex}
    alignItems={AlignItems.Center}
    role="button"
    onClick={(e) => {
      e.stopPropagation()
      onClick()
    }}
    style={{ cursor: 'pointer' }}
    data-no-code={dataNoCode?.add}
  >
    <Icon name={name} variant={IconVariant.Actionable} />
  </Box>
)

const ContainedImage = withCitrusTheme(CitrusImage, () => ({
  objectFit: 'contain',
  objectPosition: 'center',
  borderRadius: '3px',
}))

const RTHBadge = withCitrusTheme(Caption2, (theme) => ({
  color: theme.color(Color.White),
  backgroundColor: theme.color(Color.Warning_900),
  borderRadius: '3px',
  width: 'fit-content',
  padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
  marginBottom: `${theme.spacing(1)}px`,
}))

type RecipeProps = {
  recipe: Recipe
  quantity: number
  numPortions: number
  addRecipe: (id: RecipeCoreId, view: string) => void
  removeRecipe: (recipe: Recipe) => void
  onClickRecipe: (id: RecipeUUID) => void
  dataNoCode?: {
    name?: string
    servings?: string
    add?: string
  }
}

export const RecipeItem = ({
  recipe,
  quantity,
  numPortions,
  addRecipe,
  removeRecipe,
  onClickRecipe,
  dataNoCode = {},
}: RecipeProps) => {
  const image = getMoodImage(recipe.images)

  return (
    <Box
      key={recipe.id}
      display={Display.Flex}
      justifyContent={JustifyContent.FlexStart}
      alignItems={AlignItems.Center}
      gap={3}
      color={Color.ColdGrey_900}
      data-testid="recipe"
      style={{ cursor: 'pointer' }}
      onClick={() => onClickRecipe(recipe.id)}
    >
      <ContainedImage
        alt={recipe.name}
        loading="lazy"
        src={getDefaultImage(image)}
        srcSet={getSrcSet(image)}
        height="70px"
        width="70px"
      />
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.FlexStart}
        flexGrow={1}
        height="100%"
        width="100px"
      >
        {recipe.preparationType === 'heat-only' && <RTHBadge>HEAT &amp; EAT</RTHBadge>}
        <Heading6
          data-no-code={dataNoCode.name}
          style={
            recipe.preparationType === 'heat-only'
              ? {
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }
              : {}
          }
        >
          {recipe.name}
        </Heading6>
        <Box display={Display.Flex} gap={2}>
          <Box color={Color.ColdGrey_600}>
            {recipe.preparationType === 'heat-only' ? (
              <Box display={Display.Flex} alignItems={AlignItems.Center} gap={1}>
                <Icon name="microwave" variant={IconVariant.Inherit} size={[4, 6]} />
                <Body2 color={Color.Inherit} style={{ whiteSpace: 'nowrap' }}>
                  {recipe.prepTime} mins (x{numPortions})
                </Body2>
              </Box>
            ) : (
              <Body2 color={Color.Inherit} style={{ whiteSpace: 'nowrap' }}>
                {recipe.prepTime} mins
              </Body2>
            )}
          </Box>
          <Body2 color={Color.ColdGrey_200}>|</Body2>
          <Body2 color={Color.ColdGrey_600} style={{ whiteSpace: 'nowrap' }}>
            {recipe.nutritionalInformation.perPortion.energyKcal} kcal
          </Body2>
        </Box>
      </Box>
      <Box
        bg={Color.NeutralGrey_50}
        style={{
          borderRadius: '60px',
          padding: '4px 9px',
        }}
        display={Display.Flex}
        alignItems={AlignItems.Center}
        gap={2}
      >
        {quantity > 1 ? (
          <ClickableIcon name="remove" onClick={() => removeRecipe(recipe)} />
        ) : (
          <ClickableIcon name="delete_outline" onClick={() => removeRecipe(recipe)} />
        )}
        <Text fontWeight={FontWeight.Bold} data-no-code={dataNoCode.servings}>
          {quantity}
        </Text>
        <ClickableIcon
          name="add"
          onClick={() => addRecipe(recipe.coreId, 'modal_basket')}
          dataNoCode={dataNoCode}
        />
      </Box>
    </Box>
  )
}
